import { z } from "zod";

export const EventName = {
  USER_CREATED: "USER_CREATED",
  USER_LOGOUT: "USER_LOGOUT",
  USER_LOGIN: "USER_LOGIN",
  USER_SESSION_START: "USER_SESSION_START",

  TEAM_SWITCHED: "TEAM_SWITCHED",

  VIDEO_CREATED: "VIDEO_CREATED",
  VIDEO_DUPLICATED: "VIDEO_DUPLICATED",
  TP_VIDEO_CREATED: "TP_VIDEO_CREATED",
  VIDEO_UPDATED: "VIDEO_UPDATED",
  VIDEO_DOWNLOADED: "VIDEO_DOWNLOADED",
  BULK_DOWNLOAD_ANIMATED_IMAGES: "BULK_DOWNLOAD_ANIMATED_IMAGES",
  IMAGE_ADDED: "IMAGE_ADDED",
  VOICE_OVER_ADDED: "VOICE_OVER_ADDED",
  MOBILE_INTRO_VIDEO_STARTED: "MOBILE_INTRO_VIDEO_STARTED",
  ADVERTISE_VIDEO_CLICKED: "ADVERTISE_VIDEO_CLICKED",

  TUTORIAL_CLICKED: "TUTORIAL_CLICKED",
  EDITOR_OPENED: "EDITOR_OPENED",
  LISTING_SCRAPED: "LISTING_SCRAPED",
  SUGGEST_ANIMATIONS_TRIGGERED: "SUGGEST_ANIMATIONS_TRIGGERED",
  REPEATING_ANIMATION_WARNING_POPUP_OPENED:
    "REPEATING_ANIMATION_WARNING_POPUP_OPENED",
  PARTIAL_ANIMATION_POPUP_OPENED: "PARTIAL_ANIMATION_POPUP_OPENED",
  PARTIAL_ANIMATION_CONFIRMED: "PARTIAL_ANIMATION_CONFIRMED",
  ONBOARDING_SURVEY_FINISHED: "ONBOARDING_SURVEY_FINISHED",
  INSPIRATION_GUIDE_OPENED: "INSPIRATION_GUIDE_OPENED",
  IMAGE_PROMPT_USED: "IMAGE_PROMPT_USED",
  ANIMATION_PROMPT_USED: "ANIMATION_PROMPT_USED",
  DFY_LINK_CLICKED: "DFY_LINK_CLICKED",
  FEATURE_REQUESTS_LINK_CLICKED: "FEATURE_REQUESTS_LINK_CLICKED",
  DFY_ORDER_FORM_STEP_CHANGED: "DFY_ORDER_FORM_STEP_CHANGED",
  DFY_DELIVERY_DOWNLOADED: "DFY_DELIVERY_DOWNLOADED",
  DFY_ORDER_OPENED: "DFY_ORDER_OPENED",
  DFY_ORDER_CREATED: "DFY_ORDER_CREATED",
  DFY_ORDER_SUBMITTED: "DFY_ORDER_SUBMITTED",
  DFY_EXAMPLES_DIALOG_OPENED: "DFY_EXAMPLES_DIALOG_OPENED",
  DFY_EXAMPLE_PLAYED: "DFY_EXAMPLE_PLAYED",

  CREDITS_POPUP_OPENED_MANUAL: "CREDITS_POPUP_OPENED_MANUAL",
  CREDITS_POPUP_OPENED_INSUFFICIENT: "CREDITS_POPUP_OPENED_INSUFFICIENT",
  PENDING_PURCHASE_CREATED: "PENDING_PURCHASE_CREATED",
  CREDITS_PURCHASED: "CREDITS_PURCHASED",
  ANIMATE_IMAGE_TRIGGERED: "ANIMATE_IMAGE_TRIGGERED",
  CUSTOM_ANIMATION_TRIGGERED: "CUSTOM_ANIMATION_TRIGGERED",
  CUSTOM_IMAGE_EDIT_TRIGGERED: "CUSTOM_IMAGE_EDIT_TRIGGERED",
  BEAT_SYNC_TRIGGERED: "BEAT_SYNC_TRIGGERED",
  ADMIN_ADDED_CREDITS: "ADMIN_ADDED_CREDITS",
  ADMIN_ASSIGNED_ROLE: "ADMIN_ASSIGNED_ROLE",

  REFERRAL_DIALOG_OPENED: "REFERRAL_DIALOG_OPENED",
  REFERRAL_CREATED: "REFERRAL_CREATED",
  REFERRAL_REWARDED: "REFERRAL_REWARDED",
  REFERRAL_PAGE_VISITED: "REFERRAL_PAGE_VISITED",
  REFERRAL_LINK_COPIED: "REFERRAL_LINK_COPIED",

  EFS_VIDEO_CREATED: "EFS_VIDEO_CREATED",
  EFS_VIDEO_DOWNLOADED: "EFS_VIDEO_DOWNLOADED",
  EFS_ANIMATE_IMAGE_TRIGGERED: "EFS_ANIMATE_IMAGE_TRIGGERED",
  EFS_IMAGE_ADDED: "EFS_IMAGE_ADDED",
  EFS_VOICE_OVER_ADDED: "EFS_VOICE_OVER_ADDED",
  EFS_LISTING_SCRAPED: "EFS_LISTING_SCRAPED",
  EFS_CREDITS_POPUP_OPENED_MANUAL: "EFS_CREDITS_POPUP_OPENED_MANUAL",
  EFS_CREDITS_POPUP_OPENED_INSUFFICIENT:
    "EFS_CREDITS_POPUP_OPENED_INSUFFICIENT",
  EFS_SUGGEST_ANIMATIONS_TRIGGERED: "EFS_SUGGEST_ANIMATIONS_TRIGGERED",
  EFS_EXPORT_VIDEO_DIALOG_CLOSED: "EFS_EXPORT_VIDEO_DIALOG_CLOSED",
  EFS_CREATIVE_MODE_OPENED: "EFS_CREATIVE_MODE_OPENED",
  EFS_CUSTOM_IMAGE_EDIT: "EFS_CUSTOM_IMAGE_EDIT",
  EFS_CUSTOM_ANIMATE: "EFS_CUSTOM_ANIMATE",
  EFS_BEAT_SYNC_TRIGGERED: "EFS_BEAT_SYNC_TRIGGERED",
} as const;

export type EventName = (typeof EventName)[keyof typeof EventName];

export const EVENT_URL_MAPPING: Record<string, EventName> = {
  logout: EventName.USER_LOGOUT,
  login: EventName.USER_LOGIN,
  "session-start": EventName.USER_SESSION_START,
  "team-switched": EventName.TEAM_SWITCHED,
  "tutorial-clicked": EventName.TUTORIAL_CLICKED,
  "editor-opened": EventName.EDITOR_OPENED,
  "credits-popup-manual": EventName.CREDITS_POPUP_OPENED_MANUAL,
  "credits-popup-insufficient": EventName.CREDITS_POPUP_OPENED_INSUFFICIENT,
  "mobile-intro-video-started": EventName.MOBILE_INTRO_VIDEO_STARTED,
  "repeating-animation-warning-popup":
    EventName.REPEATING_ANIMATION_WARNING_POPUP_OPENED,
  "partial-animation-popup": EventName.PARTIAL_ANIMATION_POPUP_OPENED,
  "partial-animation-confirmed": EventName.PARTIAL_ANIMATION_CONFIRMED,
  "referral-dialog-opened": EventName.REFERRAL_DIALOG_OPENED,
  "referral-link-copied": EventName.REFERRAL_LINK_COPIED,
  "advertise-video-clicked": EventName.ADVERTISE_VIDEO_CLICKED,
  "inspiration-guide-opened": EventName.INSPIRATION_GUIDE_OPENED,
  "image-prompt-used": EventName.IMAGE_PROMPT_USED,
  "animation-prompt-used": EventName.ANIMATION_PROMPT_USED,
  "dfy-link-clicked": EventName.DFY_LINK_CLICKED,
  "feature-requests-link-clicked": EventName.FEATURE_REQUESTS_LINK_CLICKED,
  "beat-sync-triggered": EventName.BEAT_SYNC_TRIGGERED,
  "dfy-order-form-step-changed": EventName.DFY_ORDER_FORM_STEP_CHANGED,
  "dfy-delivery-downloaded": EventName.DFY_DELIVERY_DOWNLOADED,
  "dfy-order-opened": EventName.DFY_ORDER_OPENED,
  "dfy-examples-dialog-opened": EventName.DFY_EXAMPLES_DIALOG_OPENED,
  "dfy-example-played": EventName.DFY_EXAMPLE_PLAYED,
};

export const CreationSource = z.enum(["HOME", "VIDEOS_PAGE", "DFY_PAGE"]);
export type CreationSource = z.infer<typeof CreationSource>;

export type EventUrl = keyof typeof EVENT_URL_MAPPING;

export const AnalyticsEventData = z.object({
  initial_tab: z.string().optional(),
  required_credits: z.number().optional(),
  selected_images_count: z.number().optional(),
  animatable_images_count: z.number().optional(),
  guide_id: z.string().optional(),
  prompt_id: z.string().optional(),
  auto_navigated_from_instructions: z.boolean().optional(),
  dfy_order_external_id: z.string().optional(),
  from_step: z.string().optional(),
  to_step: z.string().nullable().optional(),
  direction: z.enum(["FORWARD", "BACKWARD"]).optional(),
  is_last_step: z.boolean().optional(),
  package_type: z.string().nullable().optional(),
  add_ons: z.array(z.string()).optional(),
  branding_templates: z.array(z.string()).optional(),
  image_selection_mode: z.string().optional(),
  delivery_external_id: z.string().optional(),
  aspect_ratio: z.string().nullable().optional(),
  upscale_quality: z.string().nullable().optional(),
  order_status: z.string().optional(),
  deliveries_count: z.number().optional(),
  source: z.string().optional(),
  tutorial_id: z.string().optional(),
  example_id: z.string().optional(),
});

export const AnalyticsEventRequestDto = z.object({
  teamExternalId: z.string().optional(),
  eventData: AnalyticsEventData.optional(),
});

export const AnalyticsEventResponseDto = z.object({
  success: z.boolean(),
});

export type AnalyticsEventData = z.infer<typeof AnalyticsEventData>;
export type AnalyticsEventRequestDto = z.infer<typeof AnalyticsEventRequestDto>;
export type AnalyticsEventResponseDto = z.infer<
  typeof AnalyticsEventResponseDto
>;
